import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, pipe, map, catchError, throwError } from "rxjs";
import { ConfigLoaderService } from 'src/app/services/config-loader.service';
import { environment } from 'src/environments/environment';

/**
 * Shape of the runtime configuration loaded from `src/assets/app.config.json`.
 *
 * The file is never committed to the repository. Each deployment environment
 * (local dev, tera, portal2) carries its own copy with environment-specific
 * server addresses, Keycloak realm, and company defaults.
 *
 * @see {@link AppConfigService.loadInitialConnectionConfiguration}
 */
export interface AppConfig {
  /** Connection strings for all external servers used by the application. */
  connection: any;
  /** Default company-level settings applied before the user's DB profile loads. */
  defaultCompanySettings: {
    defMainCompanyId: number;
    defMainPersonId: number;
    defVatValue: number;
    defMainCurrencyId: number;
    defBalanceCurrencyId: number;
    defViewDCurrencyId: number;
    defViewECurrencyId: number;
    defMainStockId: number;
    defImpStockId: number;
    defAuxStockId: number;
    defImpCountFlag: boolean;
    defAuxCountFlag: boolean;
    defManDepartmentId: number;
    defAuxDepartmentId: number;
    defPaidDepartmentId: number;
    defCashDepartmentId: number;
  };
}

/**
 * Loads and caches the deployment-time runtime configuration from
 * `src/assets/app.config.json`.
 *
 * This service is called once during the Angular `APP_INITIALIZER` bootstrap
 * sequence, before any component or service tries to read connection details.
 * The loaded `AppConfig` object is then consumed by
 * {@link GetglobalsettingsService} to populate API server URLs, the Keycloak
 * realm, and default company settings.
 *
 * @remarks
 * The `app.config.json` file is **not** committed to the repository. It must
 * be placed at `src/assets/app.config.json` in each deployment environment.
 * If the file is missing, `loadInitialConnectionConfiguration` falls back to
 * hardcoded development defaults so local development can proceed without the
 * file. The fallback must never be used in production.
 *
 * Note: all four components of the system (frontend, backend API, Keycloak,
 * jsReport) must reference the same auth server URL and realm. Changing the
 * auth server requires coordinated updates across all four.
 */
@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private config: AppConfig;
  /** `true` once `loadInitialConnectionConfiguration` has completed. */
  loaded = false;

  constructor(private http: HttpClient,
    private configLoaderService:  ConfigLoaderService) { }

  /**
   * Fetches `app.config.json` from the site assets and stores the result.
   * Called by `APP_INITIALIZER` in `app.module.ts` — must resolve before any
   * route or component renders.
   *
   * On success, sets {@link loaded} to `true` and populates the internal
   * `config` cache consumed by {@link getConfig}.
   *
   * On failure (file missing or malformed), logs a warning and applies
   * hardcoded development defaults so that local development can proceed.
   * The fallback config points to development servers and must not be used
   * in production.
   *
   * @returns Observable that completes with `void` once the config is ready.
   */
  loadInitialConnectionConfiguration(): Observable<void> {
    return this.configLoaderService.loadConfig(environment.siteConfig)
      .pipe(
        map(config => {
          this.config = config;
          this.loaded = true;
          environment.production || console.log('*** AppConfigService in loadInitialConnectionConfiguration: ' + JSON.stringify(this.config, null, 2));
        }),
        catchError((error) => {
          console.error('AppConfigService Failed to load configuration file:', error);
          console.warn('Using fallback configuration. Please create assets/app.config.json file.');

          // Provide fallback configuration
          this.config = {
            connection: {
              "dbServer": {
                "url": "SQL://SQL2019CENTOS8.disti.pro",
                "items": [
                  "SQL://168.119.252.177:21233",
                  "SQL://distipro-adc.disti.pro",
                  "SQL://POLO.disti.pro:8433",
                  "SQL://DOCKERSQL.disti.pro:21233",
                  "SQL://SQL2019CENTOS8.disti.pro"
                ],
                "dbName": "Document",
                "userLogin": "express"
              },

              "apiServer": {
                "url": "http://localhost:1337",
                "items": [
                  "http://localhost:1337",
                  "https://polo.disti.pro:8004",
                  "https://auth2.disti.pro:8004",
                  "https://webapi.disti.pro:8000",
                  "https://webapi2.disti.pro:8000"
                ]
              },

              "authServer": {
                "url": "https://sara.disti.pro:8443/auth/",
                "realm": "portal",
                "clientId": "portal-dev",
                "items": [
                  {
                    "url": "https://polo.disti.pro:8444/auth/",
                    "clientId": "portal4"
                  },
                  {
                    "url": "https://auth2.disti.pro:8444/auth/",
                    "clientId": "portal-auth2"
                  },
                  {
                    "url": "https://auth0.disti.pro:8443/auth/",
                    "clientId": "portal-auth0"
                  },
                  {
                    "url": "https://dupa.disti.pro:8443/auth/",
                    "clientId": "portal-dev"
                  }
                ]
              },

              "reportServer": {
                "url1": "{\"report1\":\"https://dupa.disti.pro:5589\", \"report2\":\"https://dupa.disti.pro:5489\", \"apiUrl\": \"/api/report/\", \"secret\": \"YWRtaW46anNBYnJ1cHQ0IQ==\"}",
                "items1": [
                  "{\"report1\":\"https://AUTH2.DISTI.PRO:8589\", \"report2\":\"https://AUTH2.DISTI.PRO:8489\", \"apiUrl\": \"/api/report/\", \"secret\": \"YWRtaW46anNBYnJ1cHQ0IQ==\"}",
                  "{\"report1\":\"https://REPORT1.DISTI.PRO:5589\", \"report2\":\"https://REPORT1.DISTI.PRO:5489\", \"apiUrl\": \"/api/report/\", \"secret\": \"\"}",
                  "{\"report1\":\"https://REPORT2.DISTI.PRO:5589\", \"report2\":\"https://REPORT2.DISTI.PRO:5489\", \"apiUrl\": \"/api/report/\", \"secret\": \"inuqaECNye9gQ90C\"}",
                  "{\"report1\":\"https://REPORT.DISTI.PRO:5589\", \"report2\":\"https://REPORT.DISTI.PRO:5489\", \"apiUrl\": \"/api/report/\", \"secret\": \"\"}"
                ],
                "url2": "https://dupa.disti.pro:5489",
                "items2": [
                  "https://auth2.DISTI.PRO:8489",
                  "https://REPORT2.DISTI.PRO:5489",
                  "https://REPORT1.DISTI.PRO:5489",
                  "https://REPORT.DISTI.PRO:5489"
                ]
              }
            },
            defaultCompanySettings: {
              defMainCompanyId: 0,
              defMainPersonId: 0,
              defVatValue: 0,
              defMainCurrencyId: 0,
              defBalanceCurrencyId: 0,
              defViewDCurrencyId: 0,
              defViewECurrencyId: 0,
              defMainStockId: 0,
              defImpStockId: 0,
              defAuxStockId: 0,
              defImpCountFlag: false,
              defAuxCountFlag: false,
              defManDepartmentId: 0,
              defAuxDepartmentId: 0,
              defPaidDepartmentId: 0,
              defCashDepartmentId: 0
            }
          };
          this.loaded = true;

          return of(void 0);
        })
      );
  }

  /**
   * Returns the loaded `AppConfig` synchronously.
   *
   * Will return `undefined` if called before `loadInitialConnectionConfiguration`
   * has completed. Check {@link loaded} or use {@link getObservableConfigNew}
   * when timing is uncertain.
   */
  getConfig(): AppConfig {
//    environment.production || console.log('*** AppConfigService in getConfig: ' + JSON.stringify(this.config, null, 2));
    return this.config;
  }

  /**
   * Returns an Observable that emits the `AppConfig` object.
   *
   * If the config has already been loaded ({@link loaded} is `true`), emits
   * the cached value immediately. Otherwise triggers a fresh HTTP fetch.
   *
   * Prefer this over {@link getConfig} in contexts where the load timing
   * relative to the caller is uncertain.
   *
   * @returns Observable emitting the `AppConfig` once available.
   */
  getObservableConfigNew(): Observable<AppConfig> {
    return (this.loaded) ?
      of(this.config).pipe(
        tap(config => {
          environment.production || console.log('*** AppConfigService in getObservablConfigNew: (cached) ' + JSON.stringify(config, null, 2));
        })
      )
      :
      this.configLoaderService.loadConfig(environment.siteConfig).pipe(
        tap(config => {
          this.config = config;
          this.loaded = true;
          environment.production || console.log('*** AppConfigService in getObservablConfigNew: (loaded) ' + JSON.stringify(this.config, null, 2));
        })
      );
  }
}
